import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Crown, Mail, Calendar, CheckCircle, Clock, AlertCircle, TrendingUp, Edit, Save, X } from 'lucide-react';
import './styles/TeamOverview.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeamOverview({ user }) {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [isMemberDetailsOpen, setIsMemberDetailsOpen] = useState(false);
  const [isAssignTaskOpen, setIsAssignTaskOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [assignToMember, setAssignToMember] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [addMemberMode, setAddMemberMode] = useState('invite'); // 'invite' or 'manual'
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  const [editedTeamName, setEditedTeamName] = useState('');

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamMembers();
      fetchTeamTasks();
    }
  }, [selectedTeam]);

  // Listen for task updates from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'tasks_updated' && selectedTeam) {
        console.log('TeamOverview: Refetching tasks due to storage change');
        fetchTeamTasks();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen to custom event for same-window updates
    const handleTaskUpdate = () => {
      if (selectedTeam) {
        console.log('TeamOverview: Refetching tasks due to custom event');
        fetchTeamTasks();
      }
    };
    
    window.addEventListener('taskUpdated', handleTaskUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('taskUpdated', handleTaskUpdate);
    };
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      if (response.data.length > 0 && !selectedTeam) {
        setSelectedTeam(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch team members');
    }
  };

  const fetchTeamTasks = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/tasks?team_id=${selectedTeam.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch team tasks');
    }
  };

  const createTeam = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/teams?team_name=${encodeURIComponent(newTeamName)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team created successfully');
      setIsCreateDialogOpen(false);
      setNewTeamName('');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/teams/${selectedTeam.id}/invite?email=${encodeURIComponent(inviteEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation sent successfully! They will receive an email.');
      setIsAddMemberDialogOpen(false);
      setInviteEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const addManualMember = async () => {
    if (!manualEmail || !manualName) {
      toast.error('Please enter both email and name');
      return;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(manualEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/teams/${selectedTeam.id}/members/manual?email=${encodeURIComponent(manualEmail)}&name=${encodeURIComponent(manualName)}`, 
        {}, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.user_exists) {
        toast.success('Existing user added to team successfully!');
      } else {
        toast.success('Member added! They will join when they register with this email.');
      }
      
      setIsAddMemberDialogOpen(false);
      setManualEmail('');
      setManualName('');
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const assignTask = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.patch(`${API}/tasks/${taskToAssign.id}`, 
        { assigned_to: assignToMember },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task assigned successfully');
      setIsAssignTaskOpen(false);
      setTaskToAssign(null);
      setAssignToMember('');
      fetchTeamTasks();
      
      // Notify other components
      console.log('TeamOverview: Dispatching task assignment event');
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
    } catch (error) {
      toast.error('Failed to assign task');
    }
  };

  const getMemberStats = (memberId) => {
    const memberTasks = teamTasks.filter(t => t.assigned_to === memberId);
    return {
      total: memberTasks.length,
      completed: memberTasks.filter(t => t.status === 'done').length,
      inProgress: memberTasks.filter(t => t.status === 'in-progress').length,
      todo: memberTasks.filter(t => t.status === 'todo').length
    };
  };

  const getTeamStats = () => {
    return {
      totalMembers: teamMembers.length,
      totalTasks: teamTasks.length,
      completedTasks: teamTasks.filter(t => t.status === 'done').length,
      activeTasks: teamTasks.filter(t => t.status !== 'done').length
    };
  };

  const openMemberDetails = (member) => {
    setSelectedMember(member);
    setIsMemberDetailsOpen(true);
  };

  const startEditingTeamName = (team) => {
    setEditedTeamName(team.name);
    setIsEditingTeamName(true);
    setSelectedTeam(team);
  };

  const updateTeamName = async () => {
    if (!editedTeamName.trim()) {
      toast.error('Team name cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.put(
        `${API}/teams/${selectedTeam.id}`, 
        { name: editedTeamName },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Team name updated successfully');
      setIsEditingTeamName(false);
      fetchTeams();
    } catch (error) {
      toast.error('Failed to update team name');
    }
  };

  const teamStats = selectedTeam ? getTeamStats() : null;

  return (
    <div className="team-overview" data-testid="team-overview">
      <div className="page-header">
        <div>
          <h1 className="page-title">Team Overview</h1>
          <p className="page-subtitle">Manage your teams and members</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-team-button">
              <Users size={20} style={{ marginRight: '0.5rem' }} />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-team-dialog">
            <DialogHeader>
              <DialogTitle>Create New Team</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Team name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                data-testid="team-name-input"
              />
              <Button onClick={createTeam} data-testid="create-team-submit">Create Team</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="teams-grid">
        {teams.map(team => (
          <Card
            key={team.id}
            className={`team-card ${selectedTeam?.id === team.id ? 'selected' : ''}`}
            onClick={() => setSelectedTeam(team)}
            data-testid={`team-card-${team.id}`}
          >
            <div className="team-card-header">
              {isEditingTeamName && selectedTeam?.id === team.id ? (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', width: '100%' }} onClick={(e) => e.stopPropagation()}>
                  <Input
                    value={editedTeamName}
                    onChange={(e) => setEditedTeamName(e.target.value)}
                    placeholder="Team name"
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); updateTeamName(); }}>
                    <Save size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setIsEditingTeamName(false); }}>
                    <X size={16} />
                  </Button>
                </div>
              ) : (
                <>
                  <h3>{team.name}</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {team.owner_id === user.id && (
                      <>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); startEditingTeamName(team); }}
                          style={{ padding: '0.25rem' }}
                        >
                          <Edit size={16} />
                        </Button>
                        <Crown size={20} className="owner-icon" />
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="team-card-stats">
              <div className="stat-item">
                <Users size={16} />
                <span>{team.members.length} members</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {selectedTeam && teamStats && (
        <div className="team-stats-section">
          <h2>Team Statistics</h2>
          <div className="stats-grid">
            <Card className="stat-box">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #1a936f 0%, #0f6b4f 100%)' }}>
                <Users size={28} />
              </div>
              <div className="stat-info">
                <h3>{teamStats.totalMembers}</h3>
                <p>Team Members</p>
              </div>
            </Card>
            <Card className="stat-box">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #26a17b 0%, #1a936f 100%)' }}>
                <CheckCircle size={28} />
              </div>
              <div className="stat-info">
                <h3>{teamStats.completedTasks}</h3>
                <p>Completed Tasks</p>
              </div>
            </Card>
            <Card className="stat-box">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #33b087 0%, #26a17b 100%)' }}>
                <Clock size={28} />
              </div>
              <div className="stat-info">
                <h3>{teamStats.activeTasks}</h3>
                <p>Active Tasks</p>
              </div>
            </Card>
            <Card className="stat-box">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #40bf93 0%, #33b087 100%)' }}>
                <TrendingUp size={28} />
              </div>
              <div className="stat-info">
                <h3>{teamStats.totalTasks}</h3>
                <p>Total Tasks</p>
              </div>
            </Card>
          </div>
        </div>
      )}

      {selectedTeam && (
        <Tabs defaultValue="members" className="team-tabs">
          <TabsList>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="tasks">Task Assignment</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="tab-content">
            <div className="members-header">
              <h2>Team Members</h2>
              {selectedTeam.owner_id === user.id && (
                <Dialog open={isAddMemberDialogOpen} onOpenChange={setIsAddMemberDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="add-member-button">
                      <UserPlus size={20} style={{ marginRight: '0.5rem' }} />
                      Add Member
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-member-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Team Member</DialogTitle>
                    </DialogHeader>
                    <Tabs value={addMemberMode} onValueChange={setAddMemberMode} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="invite" data-testid="invite-tab">Email Invite</TabsTrigger>
                        <TabsTrigger value="manual" data-testid="manual-tab">Manual Add</TabsTrigger>
                      </TabsList>
                      <TabsContent value="invite" className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Email Address</label>
                          <Input
                            type="email"
                            placeholder="colleague@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            data-testid="invite-email-input"
                          />
                          <p className="text-sm text-gray-500 mt-2">
                            We'll send them an email invitation to join your team.
                          </p>
                        </div>
                        <Button onClick={sendInvite} data-testid="send-invite-submit" disabled={!inviteEmail}>
                          Send Invitation
                        </Button>
                      </TabsContent>
                      <TabsContent value="manual" className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Name</label>
                          <Input
                            type="text"
                            placeholder="John Doe"
                            value={manualName}
                            onChange={(e) => setManualName(e.target.value)}
                            data-testid="manual-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Email Address</label>
                          <Input
                            type="email"
                            placeholder="john@example.com"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            data-testid="manual-email-input"
                          />
                          <p className="text-sm text-gray-500 mt-2">
                            This member will be added to your team. If they already have an account, they'll be added immediately. Otherwise, tasks can be assigned to them and will be visible when they register.
                          </p>
                        </div>
                        <Button onClick={addManualMember} data-testid="add-manual-submit" disabled={!manualEmail || !manualName}>
                          Add Member
                        </Button>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="members-list">
              {teamMembers.map(member => {
                const stats = getMemberStats(member.id);
                return (
                  <Card 
                    key={member.id} 
                    className="member-card" 
                    onClick={() => openMemberDetails(member)}
                    data-testid={`member-card-${member.id}`}
                  >
                    <img src={member.picture} alt={member.name} className="member-avatar" />
                    <div className="member-info">
                      <div className="member-name-section">
                        <h4>{member.name}</h4>
                        {member.is_pending && (
                          <Badge variant="secondary" className="pending-badge">
                            <Clock size={14} style={{ marginRight: '0.25rem' }} />
                            Pending
                          </Badge>
                        )}
                        {member.id === selectedTeam.owner_id && (
                          <Badge className="owner-badge">Owner</Badge>
                        )}
                      </div>
                      <p className="member-email">
                        <Mail size={14} />
                        {member.email}
                      </p>
                      {!member.is_pending ? (
                        <div className="member-stats">
                          <span className="stat-badge completed">{stats.completed} Done</span>
                          <span className="stat-badge in-progress">{stats.inProgress} In Progress</span>
                          <span className="stat-badge todo">{stats.todo} To Do</span>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mt-2">
                          Will join when they register with this email
                        </p>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="tab-content">
            <div className="tasks-assignment-section">
              <h2>Assign Tasks to Team Members</h2>
              <div className="tasks-list">
                {teamTasks.filter(t => t.status !== 'done').map(task => (
                  <Card key={task.id} className="task-assignment-card">
                    <div className="task-assignment-info">
                      <h4>{task.title}</h4>
                      <p>{task.description}</p>
                      <div className="task-meta">
                        <Badge>{task.status.replace('-', ' ')}</Badge>
                        {task.assigned_to && (
                          <span className="current-assignee">
                            Assigned to: {teamMembers.find(m => m.id === task.assigned_to)?.name || 'Unknown'}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setTaskToAssign(task);
                        setAssignToMember(task.assigned_to || '');
                        setIsAssignTaskOpen(true);
                      }}
                    >
                      {task.assigned_to ? 'Reassign' : 'Assign'}
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="performance" className="tab-content">
            <div className="performance-section">
              <h2>Team Performance</h2>
              <div className="performance-list">
                {teamMembers.map(member => {
                  const stats = getMemberStats(member.id);
                  const completionRate = stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(0) : 0;
                  return (
                    <Card key={member.id} className="performance-card">
                      <div className="performance-header">
                        <img src={member.picture} alt={member.name} className="perf-avatar" />
                        <div className="perf-info">
                          <h4>{member.name}</h4>
                          <p>{stats.total} total tasks</p>
                        </div>
                        <div className="completion-badge">
                          <span className="completion-rate">{completionRate}%</span>
                          <span className="completion-label">Completed</span>
                        </div>
                      </div>
                      <div className="performance-bars">
                        <div className="perf-bar">
                          <div className="perf-bar-label">
                            <span>Completed</span>
                            <span>{stats.completed}</span>
                          </div>
                          <div className="perf-bar-track">
                            <div 
                              className="perf-bar-fill completed"
                              style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <div className="perf-bar">
                          <div className="perf-bar-label">
                            <span>In Progress</span>
                            <span>{stats.inProgress}</span>
                          </div>
                          <div className="perf-bar-track">
                            <div 
                              className="perf-bar-fill in-progress"
                              style={{ width: `${stats.total > 0 ? (stats.inProgress / stats.total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <div className="perf-bar">
                          <div className="perf-bar-label">
                            <span>To Do</span>
                            <span>{stats.todo}</span>
                          </div>
                          <div className="perf-bar-track">
                            <div 
                              className="perf-bar-fill todo"
                              style={{ width: `${stats.total > 0 ? (stats.todo / stats.total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Member Details Dialog */}
      {selectedMember && (
        <Dialog open={isMemberDetailsOpen} onOpenChange={setIsMemberDetailsOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Member Details</DialogTitle>
            </DialogHeader>
            <div className="member-details-content">
              <div className="member-details-header">
                <img src={selectedMember.picture} alt={selectedMember.name} className="details-avatar" />
                <div>
                  <h3>{selectedMember.name}</h3>
                  <p className="details-email">{selectedMember.email}</p>
                  {selectedMember.id === selectedTeam?.owner_id && (
                    <Badge className="owner-badge">Team Owner</Badge>
                  )}
                </div>
              </div>
              <div className="member-tasks-section">
                <h4>Assigned Tasks</h4>
                {teamTasks.filter(t => t.assigned_to === selectedMember.id).map(task => (
                  <div key={task.id} className="member-task-item">
                    <div>
                      <h5>{task.title}</h5>
                      <p>{task.description}</p>
                    </div>
                    <Badge>{task.status.replace('-', ' ')}</Badge>
                  </div>
                ))}
                {teamTasks.filter(t => t.assigned_to === selectedMember.id).length === 0 && (
                  <p className="no-tasks">No tasks assigned yet</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Assign Task Dialog */}
      {taskToAssign && (
        <Dialog open={isAssignTaskOpen} onOpenChange={setIsAssignTaskOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Task: {taskToAssign.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Select value={assignToMember} onValueChange={setAssignToMember}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} {member.is_pending ? '(Pending)' : `(${getMemberStats(member.id).total} tasks)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={assignTask} disabled={!assignToMember}>
                Assign Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
